import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Notice.css';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const API_BASE = process.env.REACT_APP_API_BASE || "";

export default function Notice() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]); // 서버에서 받아온 전체 공지사항
  const [err, setErr] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();

  // 1. 언어별 데이터 선택 헬퍼
  const getLangData = (item, field) => {
    const lang = (i18n.language || 'KR').toLowerCase();
    const key = `${field}_${lang}`;
    return item[key] || item[`${field}_kr`] || item[field] || "";
  };

  const stripHtml = (html) => {
    if (!html) return "";
    // 1. 임시 엘리먼트 생성
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let text = doc.body.textContent || "";
    // 2. &nbsp; 같은 특수문자를 일반 공백으로 치환
    return text.replace(/\u00a0/g, " ").trim();
  };

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // 2. 화면 크기 감지 및 아이템 개수 결정
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ 요청하신 반응형 개수 설정
  const itemsPerPage = useMemo(() => {
    if (windowWidth <= 1024) return 3; // 태블릿 & 모바일 (1024px 이하)
    return 6;                          // 데스크탑
  }, [windowWidth]);

  // 3. API 데이터 페칭
  useEffect(() => {
    (async () => {
      try {
        setErr('');
        const res = await fetch(`${API_BASE}/api/notice_list.php`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed');
        
        const sorted = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
        setItems(sorted);
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
  }, []);

  // 4. 페이지네이션 계산 (itemsPerPage 반영)
  const { currentItems, totalPages } = useMemo(() => {
    const lastIdx = currentPage * itemsPerPage;
    const firstIdx = lastIdx - itemsPerPage;
    return {
      currentItems: items.slice(firstIdx, lastIdx),
      totalPages: Math.ceil(items.length / itemsPerPage)
    };
  }, [items, currentPage, itemsPerPage]); // itemsPerPage가 변하면 다시 계산

  // 화면 크기가 변해서 itemsPerPage가 줄어들 때, 현재 페이지가 총 페이지보다 커지는 현상 방지
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <>
    <Helmet>
      <title>배우 한지우 공지사항</title>
      <meta name="description" content="배우 한지우의 최신 소식과 공지사항을 확인하세요." />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="배우 한지우 공지사항" />
      <meta property="og:description" content="배우 한지우의 최신 소식과 공지사항을 확인하세요." />
      <meta property="og:url" content="https://hanjiwoo.com/notice" />
      <meta property="og:image" content="https://hanjiwoo.com/og/og-home.jpg" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="배우 한지우 공지사항" />
      <meta name="twitter:description" content="배우 한지우의 최신 소식과 공지사항을 확인하세요." />
      <meta name="twitter:image" content="https://hanjiwoo.com/og/og-home.jpg" />

      <link rel="canonical" href="https://hanjiwoo.com/notice" />
    </Helmet>
    <div className="sub-page-container notice-page">
      <div className="notice-header">
        <h1 className="section-title">{t('nav_notice')}</h1>
        {/* <p className="section-desc">{t('notice_info', '새로운 소식과 안내 사항을 확인하세요.')}</p> */}
      </div>

      {err && <p className="err-msg">{err}</p>}

      <div className="notice-list">
        {currentItems.length > 0 ? (
          currentItems.map((n, idx) => {
            const displayTitle = getLangData(n, 'title');
            const displayContent = getLangData(n, 'content');
            const pureText = stripHtml(displayContent);

            return (
              <div 
                key={n.id} 
                className="notice-item" 
                // onClick={() => {console.log("Current Item Data:", n); setSelectedNotice({ ...n, displayTitle, displayContent })}}
                onClick={() => navigate(`/notice/${n.id}`)}
                style={{ "--delay": idx }}
              >
                <div className="item-left">
                  <span className="notice-date">{n.date?.replace(/-/g, '.')}</span>
                  <span className="notice-tag">{n.category || "General"}</span>
                </div>
                
                <div className="item-body">
                  <h3 className="notice-title">{displayTitle}</h3>
                  {/* <p className="notice-preview">{pureText.substring(0, 120)}...</p> */}
                </div>

                <div className="item-right">
                  <div className="arrow-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="no-data">{t('no_data', '공지사항이 없습니다.')}</p>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>&lt;</button>
          {[...Array(totalPages)].map((_, i) => (
            <button 
              key={i + 1} 
              className={currentPage === i + 1 ? 'active' : ''} 
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>&gt;</button>
        </div>
      )}

      {/* 상세 모달 */}
      {/* {selectedNotice && (
        <div className="notice-modal-overlay" onClick={() => setSelectedNotice(null)}>
          <div className="notice-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-meta">
                <span className="modal-tag">{selectedNotice.category}</span>
                <span className="modal-date">{selectedNotice.date}</span>
              </div>
              <h2 className="modal-title">{selectedNotice.displayTitle}</h2>
              <button className="modal-close-btn" onClick={() => setSelectedNotice(null)}>&times;</button>
            </div>
            <div 
              className="modal-body ql-editor"
              dangerouslySetInnerHTML={{ __html: selectedNotice.displayContent }}
            />
          </div>
        </div>
      )} */}
    </div>
    </>
  );
}